/**
 * Shared list/card data table with toolbar, filters slot, pagination, and details panel.
 */

import * as React from 'react';
import * as Icons from 'lucide-react';

/** Lucide icon component type from the icons package. */
type LucideIcon = Icons.LucideIcon;
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/forms/Button';
import { SegmentedControl, type SegmentOption } from '@/components/forms/SegmentedControl';
import { DataTablePagination } from './DataTablePagination';
import { DetailsViewModal } from '@/components/common/details/DetailsViewModal';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { cn } from '@/lib/utils';
import type { ViewMode } from '@/types/data-table.types';

/** Lucide icons keyed by config icon name. */
const IconsMap = Icons as unknown as Record<string, LucideIcon>;

/** List vs cards toggle options for the table toolbar. */
const VIEW_MODE_OPTIONS: readonly SegmentOption<ViewMode>[] = [
  { value: 'list', label: 'Vue liste', icon: <Icons.List className="w-4 h-4" /> },
  { value: 'cards', label: 'Vue cartes', icon: <Icons.LayoutGrid className="w-4 h-4" /> },
];

/** Resolves sidebar menu icon and label from interface-element ids. */
function useSidebarMenu(menuIds: string | string[] | undefined): {
  icon: React.ReactNode | null;
  label: string | null;
} {
  const { elements: sidebarElements } = useInterfaceElements('sidebar');

  return React.useMemo(() => {
    if (!menuIds) return { icon: null, label: null };
    const ids = Array.isArray(menuIds) ? menuIds : [menuIds];

    for (const id of ids) {
      const el = sidebarElements.find((e) => e.elementId === id);
      if (el) {
        const iconName = el.config?.['iconName'] as string | undefined;
        const Icon = iconName ? (IconsMap[iconName] ?? null) : null;
        return {
          icon: Icon ? <Icon className="h-5 w-5" /> : null,
          label: el.elementLabel || null,
        };
      }
    }
    return { icon: null, label: null };
  }, [sidebarElements, menuIds]);
}

/** Props for {@link DataTable}. */
export interface DataTableProps<T extends { id: string }> {
  data: T[];
  totalItems: number;
  isLoading: boolean;

  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  showViewToggle?: boolean;

  currentPage: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  itemLabel: string;
  onPageChange: (page: number) => void;
  hasActiveFilters: boolean;

  toolbarActions?: React.ReactNode;

  /** Extra nodes appended at the start of the section-actions bar (before filter/export/create). */
  toolbarExtra?: React.ReactNode;

  /** Left toolbar slot: tabs / role filters (between the title and action buttons). */
  toolbarLeft?: React.ReactNode;

  filtersPanel?: React.ReactNode;

  renderTableHead: () => React.ReactNode;
  /**
   * Row cells (`<td>` via XxxRow). {@link DataTable} owns the `<tr>` (classes + ref).
   */
  renderTableRow: (item: T, index: number) => React.ReactNode;
  /** Extra CSS classes on the `<tr>` (e.g. archived, expanded). */
  rowClassName?: (item: T) => string;
  expandedRowId?: string | null;
  renderExpandedRow?: (item: T) => React.ReactNode;
  /** Column count for the expanded-row colSpan. Default: 6. */
  expandedColSpan?: number;
  expandedRowRef?: React.RefObject<HTMLTableRowElement>;
  expandedCollapseRef?: React.RefObject<HTMLDivElement>;

  renderCard: (item: T, index: number) => React.ReactNode;

  emptyIcon: LucideIcon;
  emptyText: string;
  /** When set, the empty slot shows this failed-load message instead of `emptyText`. */
  errorText?: string;
  /** Retry action shown next to `errorText`. */
  onRetry?: () => void;

  /**
   * Resolved item for the details row/modal.
   * List: if not in `data`, rendered as a separate row.
   * Cards: shown in {@link DetailsViewModal}.
   */
  expandedItem?: T | null;
  /** True while fetching the expanded item detail (shows spinner). */
  isExpandedDetailLoading?: boolean;
  /** Handle close details. */
  onCloseDetails?: () => void;
  /** Render the details content. */
  renderDetailsContent?: (item: T) => React.ReactNode;
  /** Keep the details panel open (e.g. while a report is loading before `expandedItem`). */
  isDetailsPanelOpen?: boolean;
  /** Details body when no resolved item (takes precedence over `renderDetailsContent`). */
  renderDetailsPanel?: () => React.ReactNode;

  modals?: React.ReactNode;

  containerRef?: React.RefObject<HTMLDivElement>;
  className?: string;

  /**
   * Section title — shown only when isSection=true (dashboard).
   * Hidden on management pages ({@link PageHeader} is the title).
   */
  title?: string;
  /**
   * Sidebar menu elementId(s) used to resolve the section icon.
   * An array tries IDs in order (e.g. ['menu-tutors', 'menu-students']).
   * Only applies when isSection=true and title is set.
   */
  sidebarMenuId?: string | string[];
  isSection?: boolean;
}

/** Centered spinner shown while expanded-row details load. */
function DetailsLoadingSpinner() {
  return (
    <div className="flex justify-center py-8">
      <Spinner size="lg" label="Chargement des détails..." />
    </div>
  );
}

/** Picks the details panel body: custom panel, spinner, or item content. */
function resolveDetailsBody<T>(
  renderDetailsPanel: (() => React.ReactNode) | undefined,
  isExpandedDetailLoading: boolean | undefined,
  expandedItem: T | null | undefined,
  renderDetailsContent: ((item: T) => React.ReactNode) | undefined,
): React.ReactNode {
  if (renderDetailsPanel) return renderDetailsPanel();
  if (isExpandedDetailLoading || !expandedItem) {
    return <DetailsLoadingSpinner />;
  }
  if (renderDetailsContent) return renderDetailsContent(expandedItem);
  return null;
}

/** Expanded table row that hosts details content or a loading spinner. */
function ExpandedRowPanel<T>({
  expandedColSpan,
  expandedCollapseRef,
  rowRef,
  isExpandedDetailLoading,
  expandedItem,
  renderExpandedRow,
}: Readonly<{
  expandedColSpan: number;
  expandedCollapseRef?: React.RefObject<HTMLDivElement>;
  rowRef?: React.Ref<HTMLTableRowElement>;
  isExpandedDetailLoading?: boolean;
  expandedItem?: T | null;
  renderExpandedRow: (item: T) => React.ReactNode;
}>) {
  const content =
    isExpandedDetailLoading || !expandedItem ? (
      <DetailsLoadingSpinner />
    ) : (
      renderExpandedRow(expandedItem)
    );

  return (
    <tr className="table-row-expanded" ref={rowRef}>
      <td colSpan={expandedColSpan} className="p-0">
        <div ref={expandedCollapseRef} className="table-row-collapse-content">
          {content}
        </div>
      </td>
    </tr>
  );
}

/** Empty slot of {@link DataTable}; shows a failed-load message with retry when `errorText` is set. */
function DataTableEmptyState({
  icon: Icon,
  emptyText,
  errorText,
  onRetry,
}: Readonly<{
  icon: LucideIcon;
  emptyText: string;
  errorText?: string;
  onRetry?: () => void;
}>) {
  if (errorText) {
    return (
      <div className="table-empty" role="alert">
        <Icons.AlertCircle className="table-empty-icon" />
        <p className="table-empty-text">{errorText}</p>
        {onRetry && (
          <Button type="button" variant="outline" onClick={onRetry}>
            Réessayer
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="table-empty">
      <Icon className="table-empty-icon" />
      <p className="table-empty-text">{emptyText}</p>
    </div>
  );
}

/** Table-list layout for {@link DataTable} rows and optional expanded details. */
function DataTableListView<T extends { id: string }>({
  data,
  isLoading,
  emptyIcon: EmptyIcon,
  emptyText,
  errorText,
  onRetry,
  renderTableHead,
  renderTableRow,
  rowClassName,
  expandedRowId,
  renderExpandedRow,
  expandedColSpan,
  expandedRowRef,
  expandedCollapseRef,
  expandedItem,
  isExpandedDetailLoading,
  offPageExpandedItem,
  totalItems,
  hasActiveFilters,
  currentPage,
  totalPages,
  startIndex,
  endIndex,
  itemLabel,
  onPageChange,
}: Pick<
  DataTableProps<T>,
  | 'data'
  | 'isLoading'
  | 'emptyIcon'
  | 'emptyText'
  | 'errorText'
  | 'onRetry'
  | 'renderTableHead'
  | 'renderTableRow'
  | 'rowClassName'
  | 'expandedRowId'
  | 'renderExpandedRow'
  | 'expandedColSpan'
  | 'expandedRowRef'
  | 'expandedCollapseRef'
  | 'expandedItem'
  | 'isExpandedDetailLoading'
  | 'totalItems'
  | 'hasActiveFilters'
  | 'currentPage'
  | 'totalPages'
  | 'startIndex'
  | 'endIndex'
  | 'itemLabel'
  | 'onPageChange'
> & { offPageExpandedItem: T | null }) {
  const showOffPageExpand = !!expandedRowId && !data.some((item) => item.id === expandedRowId);

  return (
    <div className="table-container">
      <div className="overflow-x-auto">
        <table className="table-modern">
          <thead>{renderTableHead()}</thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={expandedColSpan}>
                  <div className="table-empty">
                    <Spinner size="lg" label="Chargement..." />
                  </div>
                </td>
              </tr>
            )}
            {!isLoading && data.length === 0 && (
              <tr>
                <td colSpan={expandedColSpan}>
                  <DataTableEmptyState
                    icon={EmptyIcon}
                    emptyText={emptyText}
                    errorText={errorText}
                    onRetry={onRetry}
                  />
                </td>
              </tr>
            )}
            {!isLoading &&
              data.map((item, index) => (
                <React.Fragment key={item.id}>
                  <tr
                    className={cn('group animate-fade-in animate-stagger', rowClassName?.(item))}
                    style={{ animationDelay: `${index * 50}ms` }}
                    ref={expandedRowId === item.id ? expandedRowRef : null}
                  >
                    {renderTableRow(item, index)}
                  </tr>
                  {expandedRowId === item.id && renderExpandedRow && (
                    <ExpandedRowPanel
                      expandedColSpan={expandedColSpan ?? 6}
                      expandedCollapseRef={expandedCollapseRef}
                      isExpandedDetailLoading={isExpandedDetailLoading}
                      expandedItem={expandedItem}
                      renderExpandedRow={renderExpandedRow}
                    />
                  )}
                </React.Fragment>
              ))}
            {!isLoading && showOffPageExpand && (
              <>
                {offPageExpandedItem && (
                  <tr
                    ref={expandedRowRef}
                    className={cn('group', rowClassName?.(offPageExpandedItem))}
                  >
                    {renderTableRow(offPageExpandedItem, -1)}
                  </tr>
                )}
                {renderExpandedRow && (
                  <ExpandedRowPanel
                    expandedColSpan={expandedColSpan ?? 6}
                    expandedCollapseRef={expandedCollapseRef}
                    rowRef={!offPageExpandedItem ? expandedRowRef : undefined}
                    isExpandedDetailLoading={isExpandedDetailLoading}
                    expandedItem={expandedItem}
                    renderExpandedRow={renderExpandedRow}
                  />
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
      {(totalItems > 0 || hasActiveFilters) && (
        <DataTablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          startIndex={startIndex}
          endIndex={endIndex}
          itemLabel={itemLabel}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}

/** Card-grid layout for {@link DataTable} items and details panel. */
function DataTableCardsView<T extends { id: string }>({
  data,
  isLoading,
  emptyIcon: EmptyIcon,
  emptyText,
  errorText,
  onRetry,
  renderCard,
  totalItems,
  hasActiveFilters,
  currentPage,
  totalPages,
  startIndex,
  endIndex,
  itemLabel,
  onPageChange,
  detailsOpen,
  onCloseDetails,
  detailsBody,
}: Pick<
  DataTableProps<T>,
  | 'data'
  | 'isLoading'
  | 'emptyIcon'
  | 'emptyText'
  | 'errorText'
  | 'onRetry'
  | 'renderCard'
  | 'totalItems'
  | 'hasActiveFilters'
  | 'currentPage'
  | 'totalPages'
  | 'startIndex'
  | 'endIndex'
  | 'itemLabel'
  | 'onPageChange'
  | 'onCloseDetails'
> & { detailsOpen: boolean; detailsBody: React.ReactNode }) {
  return (
    <>
      <div className="table-container">
        <div className="data-cards-grid-wrapper">
          <div className="data-cards-grid">
            {isLoading && (
              <div className="col-span-full text-center p-8">
                <Spinner size="lg" label="Chargement..." />
              </div>
            )}
            {!isLoading && data.length === 0 && (
              <div className="col-span-full">
                <DataTableEmptyState
                  icon={EmptyIcon}
                  emptyText={emptyText}
                  errorText={errorText}
                  onRetry={onRetry}
                />
              </div>
            )}
            {!isLoading &&
              data.length > 0 &&
              data.map((item, index) => (
                <div
                  key={item.id}
                  className="animate-fade-in animate-stagger"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {renderCard(item, index)}
                </div>
              ))}
          </div>
        </div>
      </div>
      {(totalItems > 0 || hasActiveFilters) && (
        <div className="mt-5">
          <DataTablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            startIndex={startIndex}
            endIndex={endIndex}
            itemLabel={itemLabel}
            onPageChange={onPageChange}
          />
        </div>
      )}
      {detailsOpen && onCloseDetails && detailsBody && (
        <DetailsViewModal isOpen={true} onClose={onCloseDetails}>
          {detailsBody}
        </DetailsViewModal>
      )}
    </>
  );
}

/** Paginated data table with filters, cards, and optional details. */
export const DataTable = <T extends { id: string }>({
  data,
  totalItems,
  isLoading,
  viewMode,
  onViewModeChange,
  showViewToggle = true,
  currentPage,
  totalPages,
  startIndex,
  endIndex,
  itemLabel,
  onPageChange,
  hasActiveFilters,
  toolbarActions,
  toolbarExtra,
  toolbarLeft,
  filtersPanel,
  renderTableHead,
  renderTableRow,
  rowClassName,
  expandedRowId,
  renderExpandedRow,
  expandedColSpan = 6,
  expandedRowRef,
  expandedCollapseRef,
  renderCard,
  emptyIcon: EmptyIcon,
  emptyText,
  errorText,
  onRetry,
  expandedItem,
  isExpandedDetailLoading,
  onCloseDetails,
  renderDetailsContent,
  isDetailsPanelOpen,
  renderDetailsPanel,
  modals,
  containerRef,
  className,
  title,
  sidebarMenuId,
  isSection = false,
}: DataTableProps<T>) => {
  /** Details target that is not on the current page — rendered as an extra list row. */
  const offPageExpandedItem =
    expandedItem && !data.some((item) => item.id === expandedItem.id) ? expandedItem : null;

  const detailsOpen =
    isDetailsPanelOpen ?? !!(expandedRowId && (isExpandedDetailLoading || expandedItem));
  const detailsBody = resolveDetailsBody(
    renderDetailsPanel,
    isExpandedDetailLoading,
    expandedItem,
    renderDetailsContent,
  );

  const sidebarMenu = useSidebarMenu(isSection ? sidebarMenuId : undefined);
  const sectionIcon = sidebarMenu.icon;
  /** Sidebar `element_label` when set; `||` also falls back on empty DB strings. */
  const displayTitle = sidebarMenu.label || title;
  const showSectionTitle = Boolean(isSection && displayTitle);
  const showToolbarActions = Boolean(toolbarExtra || toolbarActions || showViewToggle);

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {(showSectionTitle || showToolbarActions) && (
        <div
          className={showSectionTitle ? 'section-header' : 'flex items-center justify-end gap-2'}
        >
          {showSectionTitle && (
            <div className="flex items-center gap-2 text-[var(--color-1-nuance-1)]">
              {sectionIcon}
              <h2 className="section-title">{displayTitle}</h2>
            </div>
          )}
          {toolbarLeft && <div className="flex items-center gap-2">{toolbarLeft}</div>}
          {showToolbarActions && (
            <div className="section-actions">
              {toolbarExtra}
              {toolbarActions}
              {showViewToggle && (
                <SegmentedControl<ViewMode>
                  preset="view-toggle"
                  value={viewMode}
                  onChange={onViewModeChange}
                  options={VIEW_MODE_OPTIONS}
                />
              )}
            </div>
          )}
        </div>
      )}

      {filtersPanel}

      <div ref={containerRef}>
        {viewMode === 'list' ? (
          <DataTableListView
            data={data}
            isLoading={isLoading}
            emptyIcon={EmptyIcon}
            emptyText={emptyText}
            errorText={errorText}
            onRetry={onRetry}
            renderTableHead={renderTableHead}
            renderTableRow={renderTableRow}
            rowClassName={rowClassName}
            expandedRowId={expandedRowId}
            renderExpandedRow={renderExpandedRow}
            expandedColSpan={expandedColSpan}
            expandedRowRef={expandedRowRef}
            expandedCollapseRef={expandedCollapseRef}
            expandedItem={expandedItem}
            isExpandedDetailLoading={isExpandedDetailLoading}
            offPageExpandedItem={offPageExpandedItem}
            totalItems={totalItems}
            hasActiveFilters={hasActiveFilters}
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={startIndex}
            endIndex={endIndex}
            itemLabel={itemLabel}
            onPageChange={onPageChange}
          />
        ) : (
          <DataTableCardsView
            data={data}
            isLoading={isLoading}
            emptyIcon={EmptyIcon}
            emptyText={emptyText}
            errorText={errorText}
            onRetry={onRetry}
            renderCard={renderCard}
            totalItems={totalItems}
            hasActiveFilters={hasActiveFilters}
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={startIndex}
            endIndex={endIndex}
            itemLabel={itemLabel}
            onPageChange={onPageChange}
            detailsOpen={detailsOpen}
            onCloseDetails={onCloseDetails}
            detailsBody={detailsBody}
          />
        )}
      </div>

      {modals}
    </div>
  );
};
